import { useState, useEffect, useCallback, useRef } from "react";
import { ApiHelper } from "../../utils/ApiHelper";
import { GROUP_CHAT_LIST, REQUIREMENTS_DATA } from "../../lib/API_KEYS";

// Format date utility (for consistent formatting)
export const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
};

export const useProjectData = (search, currentPage, perPage, statusFilter = null, viewType = null) => {
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [selectedRows, setSelectedRows] = useState({});
    const [totalResults, setTotalResults] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [availableStatuses, setAvailableStatuses] = useState([]);
    const abortControllerRef = useRef(null);

    const fetchProjects = useCallback(async () => {
        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setLoading(true);
        try {
            // Prepare status filter for backend (exclude "All")
            const statusParam = statusFilter && !statusFilter.includes("All") ? { status: statusFilter } : {};

            // Add view parameter for admin contracts/projects
            const viewParam = { view: viewType };

            const response = await new ApiHelper().FetchFromServer(
                ...GROUP_CHAT_LIST,
                {
                    ...(search ? { search } : {}),
                    page: currentPage,
                    limit: perPage,
                    ...statusParam,
                    ...viewParam
                }
            );

            // Only update state if this request hasn't been cancelled
            if (!signal.aborted) {
                const results = response.data?.results || [];
                setProjects(results);
                setTotalResults(response.data?.totalResults || results.length);
                setTotalPages(response.data?.totalPages || 1);
                if (Array.isArray(response.data?.availableStatuses)) {
                    setAvailableStatuses(response.data.availableStatuses);
                }
            }
        } catch (err) {
            // Don't log abort errors
            if (err.name !== 'AbortError') {
                console.error("Error fetching projects:", err);
                if (!signal.aborted) {
                    setProjects([]);
                    setTotalResults(0);
                    setTotalPages(1);
                }
            }
        } finally {
            if (!signal.aborted) {
                setLoading(false);
            }
        }
    }, [search, currentPage, perPage, statusFilter, viewType]);

    useEffect(() => {
        fetchProjects();
    }, [search, currentPage, perPage, statusFilter, viewType]);

    // --- Selection logic ---
    const startIdx = (currentPage - 1) * perPage;
    const allSelected = projects.every(
        (_, i) => selectedRows[startIdx + i]
    );

    const toggleAll = (checked) => {
        const updated = { ...selectedRows };
        projects.forEach((_, i) => {
            updated[startIdx + i] = checked;
        });
        setSelectedRows(updated);
    };

    const toggleRow = (index, checked) => {
        setSelectedRows((prev) => ({ ...prev, [index]: checked }));
    };

    return {
        loading,
        projects,
        totalResults,
        totalPages,
        availableStatuses,
        selectedRows,
        toggleAll,
        toggleRow,
        formatDate,
    };
};
